/** The dashboard's entry: the mock worker (plus the v2 handlers) when the page runs on demo data, Brightmark's runtime context, then the router. The stylesheet (kit/kit.css) is linked from frontend/index.html. */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { installBrightmarkTelemetry } from "../api/feedback";
import { App } from "./app/App";
import { DEMO } from "./app/demo";
import { onGalleryPage } from "./app/replica";

/**
 * Start MSW with src/mocks' enableMocks(), then put the v2 fixtures in front.
 * Imported only in demo mode: src/mocks re-exports its handlers statically, so a static import
 * would ship MSW and every fixture to production.
 */
async function startMocks(): Promise<void> {
  if (!DEMO || onGalleryPage()) return;
  const { enableMocks } = await import("../mocks");
  await enableMocks();
  const [{ worker }, { v2Handlers }, { panelHandlers }, { login }] = await Promise.all([
    import("../mocks/browser"),
    import("./mocks/v2-handlers"),
    import("./panel/mocks"),
    import("../api/client"),
  ]);
  worker.use(...v2Handlers, ...panelHandlers);
  // Settings, Data quality shows its one possible duplicate (#731).
  (await import("./mocks/roster")).seedDemoDuplicate();
  // The demo opens signed in as the mock layer's demo user, so authenticated mocks answer; signing out shows the sign-in form.
  await login("demo", "KeystoneDemo!2026").catch(() => undefined);
}

// Light only (#726): the classic dashboard kept a dark theme in localStorage. Nothing reads it now; it is cleared, not left to linger.
try {
  localStorage.removeItem("keystone-theme");
} catch {
  // Storage blocked: there is nothing stored to clear.
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element #root not found");

void startMocks().then(() => {
  // Brightmark runtime context (#587): the console and API ring buffers start before the first
  // render, after the mock worker has patched fetch, so they see the responses the app gets. In
  // memory only; a Feedback report drains them (captureClientHints), nothing is stored.
  installBrightmarkTelemetry();
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
